/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Icon, Statistic, StatisticLabel, StatisticValue, Transition, Button } from 'semantic-ui-react'
import { CardContent } from '../../../Shared/Card'
import React, { useState } from 'react'
import useUser from '../../../../Shared/UserProvider/useUser'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { CardTitle } from '../../../Shared/CardTitle'
import { intlFormatMoneyWithCompact, ONE_THOUSAND } from '../../../../../helpers/money'
import { Link } from 'react-router-dom-v5-compat'
import { useTranslation } from 'react-i18next'
import { MissedRevenueBreakdownBar } from './MissedRevenueBreakdownBar'
import { RateRadialChart } from '../../../../Shared/RateRadialChart'
import { THEME_COLORS } from '../../../../Shared/theme'

const sectionTitleCss = css`
    justify-self: center;
    font-weight: 600;
    color: rgba(0, 0, 0, .87);
    > i {
        margin-left: .5rem;
    }
`

const getColor = (rate) => {
    if (rate === 0) return '#e5e7eb'
    if (rate === 100) return THEME_COLORS.GREEN
    if (rate >= 50) return THEME_COLORS.ORANGE
    return THEME_COLORS.RED
}

const MissedRevenueCardContent = ({
    from,
    to,
    totalMissedRevenue,
    revenuesMissed,
    missedOrder,
    noNegativeActivity,
    totalMissedRevenueUnavailable,
    totalMissedRevenueSubstituted,
    noUnavailableItems,
}) => {
    const [t] = useTranslation()
    const {user} = useUser()
    const currency = user.getCompanyCurrency()
    const locale = user.getCompanyLocale()
    const [showChart, setShowChart] = useState(false)

    const fmt = (amount) => intlFormatMoneyWithCompact(amount, currency, locale, ONE_THOUSAND)

    const transitionAnimation = 'fade left'
    const transitionDuration = 300

    return (
      <CardContent
        css={css`
            display: grid;
            place-items: center;
            gap: .35rem;
            grid-template-columns: 1fr;
            grid-template-rows: 30px repeat(3, 1fr) 100px;
            grid-template-areas:
                'title'
                'a'
                'block1'
                'middle'
                'breakdown';
        `}
      >
          <CardTitle title={format(new Date(), 'MMMM', {locale: fr})}/>

          <Statistic
            color="red"
            size="small"
            css={css`grid-area: a;
                &.ui.statistic, &.ui.statistic + .ui.statistic {
                    margin: 0;
                }`}
          >
              <StatisticValue>{fmt(totalMissedRevenue)}</StatisticValue>
              <StatisticLabel>{t('dashboard.missed_revenues.total_missed_revenues')}</StatisticLabel>
          </Statistic>

          <div css={css`
              grid-area: block1;
              display: grid;
              place-items: center;
              gap: .5rem;
              width: 100%;
              grid-template-columns: 1fr 1fr;
              grid-template-rows: auto auto;
              grid-template-areas:
                  'title1 title1'
                  'c      b';
          `}>
              <Link
                to={{ pathname: '/analytics/missed', search: new URLSearchParams({ from, to }).toString() }}
                css={css`grid-area: title1; ${sectionTitleCss.styles}`}
              >
                  {t('dashboard.missed_revenues.not_acquired_orders', { count: missedOrder })}
                  <Icon name="arrow right"/>
              </Link>

              {noNegativeActivity ? (
                <div css={css`
                    grid-area: c / c / b / b;
                    display: flex;
                    align-items: center;
                    gap: .5rem;
                `}>
                    <Icon name="checkmark" color="green" />
                    <span css={css`font-weight: bold; font-size: .9rem;`}>{t('dashboard.missed_revenues.all_orders_acquired')}</span>
                </div>
              ) : (
                <>
                    <Statistic
                      color={revenuesMissed > 0 ? 'red' : undefined}
                      size="tiny"
                      css={css`&.ui.statistic, &.ui.statistic + .ui.statistic { margin: 0; }`}
                    >
                        <StatisticValue>{fmt(revenuesMissed)}</StatisticValue>
                        <StatisticLabel>{t('dashboard.missed_revenues.missed_revenues')}</StatisticLabel>
                    </Statistic>
                    <Statistic
                      color={missedOrder > 0 ? 'red' : undefined}
                      size="mini"
                      css={css`grid-area: b; &.ui.statistic, &.ui.statistic + .ui.statistic { margin: 0; }`}
                    >
                        <StatisticValue>{missedOrder}</StatisticValue>
                        <StatisticLabel>{t('dashboard.missed_revenues.not_acquired_orders', { count: missedOrder })}</StatisticLabel>
                    </Statistic>
                </>
              )}
          </div>

          <div css={css`
              grid-area: middle;
              width: 100%;
              position: relative;
              min-height: 110px;
          `}>
              <div css={css`display: flex; justify-content: center; align-items: center; padding-top: .5rem;`}>
                  <Link
                    to={{ pathname: '/analytics/unavailable-items', search: new URLSearchParams({ from, to }).toString() }}
                    css={css`${sectionTitleCss.styles}`}
                  >
                      {t('unavailable_items.header')}
                      <Icon name="arrow right"/>
                  </Link>
                  <Button
                    icon
                    size="tiny"
                    basic
                    onClick={() => setShowChart(v => !v)}
                    title={showChart ? t('unavailable_items.header') : 'Taux de substitution'}
                    css={css`&.ui.button { margin-left: .5rem; }`}
                  >
                      <Icon name={showChart ? 'chart bar' : 'chart pie'} />
                  </Button>
              </div>

              <Transition visible={!showChart} animation={transitionAnimation} duration={transitionDuration} unmountOnHide>
                  <div css={css`position: absolute; top: 2rem; left: 0; right: 0;`}>
                      <div css={css`
                          display: grid;
                          place-items: center;
                          gap: .5rem;
                          width: 100%;
                          grid-template-columns: 1fr 1fr;
                          margin-top: .8rem;
                      `}>
                        {noUnavailableItems ? (
                        <div css={css`
                            grid-column: 1 / -1;
                            display: flex;
                            align-items: center;
                            gap: .5rem;
                        `}>
                            <Icon name="checkmark" color="green" />
                            <span css={css`font-weight: bold; font-size: .9rem;`}>{t('unavailable_items.no_unavailable_product')}</span>
                        </div>
                        ) : (
                        <>
                            <Statistic
                              color={totalMissedRevenueUnavailable > 0 ? 'red' : undefined}
                              size="tiny"
                              css={css`&.ui.statistic, &.ui.statistic + .ui.statistic { margin: 0; }`}
                            >
                                <StatisticValue>{fmt(totalMissedRevenueUnavailable)}</StatisticValue>
                                <StatisticLabel>{t('dashboard.missed_revenues.removed_items')}</StatisticLabel>
                            </Statistic>
                            <Statistic
                              color={totalMissedRevenueSubstituted > 0 ? 'green' : undefined}
                              size="mini"
                              css={css`&.ui.statistic, &.ui.statistic + .ui.statistic { margin: 0; }`}
                            >
                                <StatisticValue>{fmt(totalMissedRevenueSubstituted)}</StatisticValue>
                                <StatisticLabel>{t('dashboard.missed_revenues.removed_items_of_which_substituted')}</StatisticLabel>
                            </Statistic>
                        </>
                      )}
                      </div>
                  </div>
              </Transition>

              <Transition visible={showChart} animation={transitionAnimation} duration={transitionDuration} unmountOnHide>
                  <div css={css`position: absolute; top: 2rem; left: 0; right: 0;`}>
                      <div css={css`display: flex; justify-content: center; align-items: center; width: 100%;`}>
                      <RateRadialChart
                        label='Taux de substitution'
                        rate={totalMissedRevenueUnavailable > 0 ? Math.round((totalMissedRevenueSubstituted / totalMissedRevenueUnavailable) * 100) : 0}
                        animationsEnabled
                        getColor={getColor}
                      />
                      </div>
                  </div>
              </Transition>
          </div>

          <div css={css`
              grid-area: breakdown;
              align-self: end;
              width: 100%;
          `}>
              <MissedRevenueBreakdownBar
                revenuesMissed={revenuesMissed}
                totalMissedRevenueUnavailable={totalMissedRevenueUnavailable}
                totalMissedRevenueSubstituted={totalMissedRevenueSubstituted}
                formatFn={fmt}
              />
          </div>
      </CardContent>
    )
}

export { MissedRevenueCardContent }
